import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import * as React from 'react';
import { useEffect, useState } from 'react';
import withStyles from 'react-jss';
import Asterisk from 'src/components/common/Asterisk';
import { DropdownMasterValues } from 'src/dto/response/getTaskDetails';
import { StylesProps } from 'src/Library/Types';
import { numberFormatted } from 'src/utils/utils';
import styles from './SEDeviationReasonModal.styles';

export interface DeviationInfo {
  reason: string;
  remarks: string;
}

interface SEDeviationReasonModalProps extends StylesProps<ReturnType<typeof styles>> {
  visible: boolean;
  totalQuoteAmount: number;
  totalDeviation: number;
  /** dropdownMasterId of the L1 Deviation Reason field (SMF425), used to look up its options. */
  reasonDropdownMasterId?: string;
  dropdownMasterValues: DropdownMasterValues;
  /** Pre-fill values when the popup is re-opened after a failed submit. */
  initialValue?: DeviationInfo | null;
  isSubmitting?: boolean;
  onConfirm: (info: DeviationInfo) => void;
  onCancel: () => void;
}

const formatAmount = (amount: number): string =>
  numberFormatted(amount, { places: 2, thousandSeparated: true });

/**
 * Deviation-justification popup for the Quotes Selection task (SMT71). Shown on final submit
 * (Mark Done) only when the total L1 deviation of the selected quotes is non-zero. Collects the
 * L1 Deviation Reason (mandatory) + Deviation Remarks; the caller injects these into the task-update
 * payload (fields SMF425 / SMF426).
 */
const SEDeviationReasonModal: React.FC<SEDeviationReasonModalProps> = ({
  classes,
  visible,
  totalQuoteAmount,
  totalDeviation,
  reasonDropdownMasterId,
  dropdownMasterValues,
  initialValue,
  isSubmitting,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [remarks, setRemarks] = useState<string>('');

  // Re-seed from any previously-captured values whenever the popup (re)opens.
  useEffect(() => {
    if (visible) {
      setReason(initialValue?.reason || undefined);
      setRemarks(initialValue?.remarks || '');
    }
  }, [visible]);

  const reasonOptions = (reasonDropdownMasterId && dropdownMasterValues?.[reasonDropdownMasterId]) || [];

  const handleOk = () => {
    if (!reason) return;
    onConfirm({ reason, remarks });
  };

  return (
    <Modal
      visible={visible}
      title="L1 Deviation Justification"
      okText="Confirm & Submit"
      cancelText="Cancel"
      onOk={handleOk}
      onCancel={onCancel}
      okButtonProps={{ disabled: !reason, loading: isSubmitting }}
      maskClosable={false}
    >
      <div className={classes.summary}>
        <div className={classes.summaryBlock}>
          <span className={classes.summaryLabel}>Total Quote Amount</span>
          <span className={classes.summaryValue}>INR {formatAmount(totalQuoteAmount)}</span>
        </div>
        <div className={classes.summaryBlock}>
          <span className={classes.summaryLabel}>Total L1 Deviation</span>
          <span className={classes.summaryValue}>INR {formatAmount(totalDeviation)}</span>
        </div>
      </div>

      <div className={classes.field}>
        <label className={classes.fieldLabel}>
          L1 Deviation Reason <Asterisk />
        </label>
        <Select
          className={classes.select}
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder="Select a reason"
          value={reason}
          onChange={(value: string) => setReason(value)}
          dropdownMatchSelectWidth={false}
        >
          {reasonOptions.map((option) => (
            <Select.Option key={option.id} value={option.value}>
              {option.value}
            </Select.Option>
          ))}
        </Select>
      </div>

      <div className={classes.field}>
        <label className={classes.fieldLabel}>Deviation Remarks</label>
        <Input.TextArea
          rows={3}
          placeholder="Type here..."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>
    </Modal>
  );
};

export default withStyles(styles)(SEDeviationReasonModal);
